import LegalLayout, { Section, Note } from "./LegalLayout";
import { BRAND } from "@/data/content";
import SEO from "@/components/SEO";

const UPDATED = "December 2025";

export default function Privacy() {
  return (
    <LegalLayout eyebrow="Legal · 01" title="Privacy Policy" updated={UPDATED}>
      <SEO
        title="Privacy Policy"
        description="How NEXTGEN PERFORMANCE collects, uses and protects the personal information you share with us."
        url="https://nextgenlifestyle365.com/privacy"
      />
      <p data-testid="privacy-intro" className="text-white/75 font-light leading-relaxed">
        {BRAND.name} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) respects your privacy. This
        Privacy Policy explains what personal information we collect, how we use
        it, and the choices you have. By using this site or engaging our
        services, you agree to the practices described below.
      </p>

      <Section title="1. Information We Collect">
        <p><strong className="text-white">Information you give us directly:</strong> your
        name, email, phone number, program preference, health/fitness goals,
        and any messages you send through the site&rsquo;s contact, booking,
        peptide-inquiry, or newsletter forms.</p>
        <p><strong className="text-white">Payment information:</strong> when you subscribe
        to a paid program, payment card details are collected and processed
        directly by <a href="https://stripe.com/privacy" className="text-ng-blue hover:underline" target="_blank" rel="noreferrer">Stripe</a>.
        We never see or store full card numbers.</p>
        <p><strong className="text-white">Automatic information:</strong> your IP address,
        device type, browser, referring page, and pages visited on our site.
        We use standard server logs and may use privacy-friendly analytics
        cookies.</p>
      </Section>

      <Section title="2. How We Use Your Information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To respond to inquiries and provide coaching or program information you requested.</li>
          <li>To deliver programs, schedule check-ins, and communicate about your progress.</li>
          <li>To process payments, invoicing, and refunds (if applicable) via Stripe.</li>
          <li>To send transactional emails and, if you opt in, marketing/newsletter emails.</li>
          <li>To improve our services, our website, and our client experience.</li>
          <li>To comply with legal obligations and enforce our Terms of Service.</li>
        </ul>
      </Section>

      <Section title="3. Third-Party Service Providers">
        <p>We use trusted third-party services to run our business:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Stripe</strong> — payment processing (see <a href="https://stripe.com/privacy" className="text-ng-blue hover:underline" target="_blank" rel="noreferrer">stripe.com/privacy</a>)</li>
          <li><strong className="text-white">Resend</strong> — transactional and marketing email delivery (see <a href="https://resend.com/legal/privacy-policy" className="text-ng-blue hover:underline" target="_blank" rel="noreferrer">resend.com/legal/privacy-policy</a>)</li>
          <li><strong className="text-white">MongoDB Atlas</strong> — encrypted database hosting for form submissions and account data</li>
        </ul>
        <p>Each of these providers is contractually required to protect your data and use it solely to perform services on our behalf.</p>
      </Section>

      <Section title="4. Peptide-Inquiry Data">
        <p>If you submit an inquiry through the NextGen Pro Peptides section of
        the site, we collect only what you provide (name, email, phone, the
        specific compound of interest, and any notes). We use this information
        <strong className="text-white"> solely</strong> to respond to your inquiry.
        We do not sell, share, or resell peptide inquiry data to any third
        party. See our{" "}
        <a href="/medical-disclaimer" className="text-ng-blue hover:underline">Medical Disclaimer</a>{" "}
        for important research-use-only limitations.</p>
      </Section>

      <Section title="5. Cookies & Tracking">
        <p>We use minimal cookies to keep the site working (e.g., remembering an admin session) and, where enabled, privacy-respecting analytics to understand how visitors use the site. You can disable cookies in your browser at any time; some site features may not function fully as a result.</p>
      </Section>

      <Section title="6. Data Retention">
        <p>We retain lead, booking, peptide-inquiry, and payment records for as long as necessary to provide our services, meet tax and legal obligations, and enforce our Terms. You may request deletion at any time by emailing <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a>.</p>
      </Section>

      <Section title="7. Your Rights">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Access</strong> a copy of the personal information we hold about you.</li>
          <li><strong className="text-white">Correct</strong> inaccurate or incomplete information.</li>
          <li><strong className="text-white">Delete</strong> your personal information (subject to legal recordkeeping obligations).</li>
          <li><strong className="text-white">Opt out</strong> of marketing emails via the unsubscribe link in any email we send.</li>
          <li><strong className="text-white">Object or restrict</strong> certain uses of your data.</li>
        </ul>
        <p>To exercise any of these rights, email us at <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a>.</p>
      </Section>

      <Section title="8. Security">
        <p>We take reasonable technical and organizational measures to protect your information, including TLS encryption in transit and access-controlled encrypted database storage. No system is 100% secure; we cannot guarantee absolute security, but we work continuously to protect your data.</p>
      </Section>

      <Section title="9. Children">
        <p>Our services are intended for adults 18 years of age or older. We do not knowingly collect information from anyone under 18. If you believe a minor has provided us information, please contact us and we will delete it.</p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. The &ldquo;Last Updated&rdquo; date at the top will reflect the most recent revision. Continued use of the site or our services after a change constitutes acceptance of the updated policy.</p>
      </Section>

      <Section title="11. Contact">
        <p>Questions or requests regarding your privacy?</p>
        <p>
          {BRAND.name}<br/>
          {BRAND.city} &middot; {BRAND.region}<br/>
          <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a><br/>
          <a href={`tel:${BRAND.phone_link}`} className="text-ng-blue hover:underline">{BRAND.phone}</a>
        </p>
      </Section>

      <Note>
        This Privacy Policy is a general template and does not constitute legal
        advice. For jurisdiction-specific requirements (GDPR, CCPA, HIPAA, etc.)
        we recommend consulting a licensed attorney.
      </Note>
    </LegalLayout>
  );
}
